(() => {
  if (window.SaunaConfiguratorCheckout) return;

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const normalizeWood = (value) => {
    const wood = normalize(value);
    const aliases = {
      'thermo norwegian spruce': 'norwegian nordic spruce',
      'rustic canadian cedar': 'canadian rustic red cedar',
      'clear canadian cedar': 'canadian clear red cedar'
    };
    return aliases[wood] || wood;
  };

  const formatMoney = (amount) => '$' + Number(amount || 0).toLocaleString('en-AU');

  function readPricingData(root) {
    const dataElement = root.querySelector('[data-sauna-checkout-data]');
    if (!dataElement) throw new Error('Sauna checkout data is missing.');
    return JSON.parse(dataElement.textContent);
  }

  function readSelection(root, spec, priceKey) {
    const radio = root.querySelector(`input[name="${spec.name}"]:checked`);
    const select = root.querySelector(`select[name="${spec.name}"]`);
    const input = radio || select;
    if (!input) return null;

    const priceElement = select ? select.options[select.selectedIndex] : radio;
    const value = select ? select.value : (radio.dataset.label || radio.value);
    const price = Number(priceElement?.dataset?.[priceKey] ?? priceElement?.dataset?.price ?? 0);

    return {
      name: spec.name,
      label: spec.label,
      value,
      price: Number.isFinite(price) ? price : 0
    };
  }

  function resolveBaseVariant(variants, selections) {
    const available = variants.filter((variant) => variant.available !== false);
    const pool = available.length ? available : variants;
    const wood = selections.find((selection) => selection.name === 'wood_type');
    const targetWood = normalizeWood(wood?.value);
    let candidates = pool.filter((variant) => {
      const text = normalize([variant.title, ...(variant.options || [])].join(' '));
      return targetWood && text.includes(targetWood);
    });

    if (!candidates.length) candidates = pool;

    const heater = normalize(selections.find((selection) => selection.name === 'heater_type')?.value);
    const controls = normalize(selections.find((selection) => selection.name === 'heater_controls')?.value);
    const heaterToken = heater.includes('glow') ? 'harvia glow' :
      heater.includes('wood stove') ? '' : 'harvia the wall';
    const controlsToken = controls.includes('manual') ? 'manual controls' :
      controls.includes('generic') ? 'wifi controls generic' :
      controls.includes('harvia') ? 'wifi controls harvia' : '';

    const exact = candidates.find((variant) => {
      const finalOption = normalize((variant.options || []).slice(-1)[0] || variant.title);
      return (!heaterToken || finalOption.includes(heaterToken)) &&
        (!controlsToken || finalOption.includes(controlsToken));
    });

    return exact || candidates[0];
  }

  function createCartItems({
    root,
    productName,
    selectionSpecs,
    priceKey = 'price',
    woodPricing = 'variant',
    woodBasePrice = 0,
    excludedSelections = []
  }) {
    const data = readPricingData(root);
    const selections = selectionSpecs
      .filter((spec) => !excludedSelections.includes(spec.name))
      .map((spec) => readSelection(root, spec, priceKey))
      .filter(Boolean);
    const baseVariant = resolveBaseVariant(data.productVariants || [], selections);

    if (!baseVariant?.id) throw new Error('No matching Shopify product variant is available.');

    const upgrades = [];
    selections.forEach((selection) => {
      if (selection.name === 'delivery') return;

      let amount = selection.price;
      if (selection.name === 'wood_type') {
        amount = woodPricing === 'upgrade' ? selection.price - woodBasePrice : 0;
      }

      if (amount > 0) upgrades.push({ ...selection, amount });
    });

    const configurationId = `sauna-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const delivery = selections.find((selection) => selection.name === 'delivery');
    const deliveryCharge = delivery?.price || 0;
    const productTotal = Number(baseVariant.price) / 100 +
      upgrades.reduce((sum, upgrade) => sum + upgrade.amount, 0);
    const properties = {};

    selections.forEach((selection) => {
      if (selection.name === 'delivery') return;
      properties[selection.label] = selection.value;
    });

    const deliveryInput = root.querySelector('[data-delivery-price]');
    properties['Delivery Postcode'] = deliveryInput?.dataset.postcode || '';
    properties['Delivery Estimate'] = `${formatMoney(deliveryCharge)} (confirmed at checkout from delivery address)`;
    properties['_configuration_id'] = configurationId;

    const items = [{
      id: Number(baseVariant.id),
      quantity: 1,
      properties
    }];

    upgrades.forEach((upgrade) => {
      const priceInCents = String(Math.round(upgrade.amount * 100));
      const addonVariantId = data.addonVariants?.[priceInCents];
      if (!addonVariantId) {
        throw new Error(`Missing sauna add-on variant priced at ${formatMoney(upgrade.amount)}.`);
      }

      items.push({
        id: Number(addonVariantId),
        quantity: 1,
        properties: {
          'Configuration Upgrade': `${upgrade.label}: ${upgrade.value}`,
          'For': productName,
          '_configuration_id': configurationId,
          '_sauna_configurator_addon': 'true'
        }
      });
    });

    return {
      items,
      productTotal,
      deliveryCharge,
      estimatedTotal: productTotal + deliveryCharge
    };
  }

  window.SaunaConfiguratorCheckout = Object.freeze({ createCartItems, formatMoney });
})();
